/*
 * Copyright (c) 2024 Cozy Intellect Inc.
 * Web FLAC Recording State Management
 *
 * Integrates FlacStreamRecorder with Svelte stores for UI state management.
 * Streams audio to transcription endpoint and aggregates transcripts in real-time.
 */

import { writable, get, derived } from 'svelte/store';
import {
  FlacStreamRecorder,
  type FlacStreamState,
  type TranscriptChunk
} from './flacStreamRecorder';

export interface WebFlacRecordingSession {
  transcriptId: string | null;
  state: FlacStreamState;
  deviceId: string | null;
  deviceName: string | null;
  startTime: number | null;
  duration: number;
  error: string | null;
  audioBlob: Blob | null;
  // Transcription state
  fullTranscript: string;
  transcriptChunks: TranscriptChunk[];
  lastChunkId: string | null;
  isTranscribing: boolean;
}

const initialSession: WebFlacRecordingSession = {
  transcriptId: null,
  state: 'idle',
  deviceId: null,
  deviceName: null,
  startTime: null,
  duration: 0,
  error: null,
  audioBlob: null,
  fullTranscript: '',
  transcriptChunks: [],
  lastChunkId: null,
  isTranscribing: false
};

export interface WebFlacRecordingOptions {
  transcriptionEndpoint?: string;
  chunkDurationMs?: number;
  authToken?: string;
}

/**
 * Create the FLAC recording state store with transcription support
 */
const createWebFlacRecordingStore = () => {
  const { subscribe, set, update } = writable<WebFlacRecordingSession>(initialSession);
  let recorder: FlacStreamRecorder | null = null;
  let pendingTranscriptions = 0;

  return {
    subscribe,

    /**
     * Start a new recording session with FLAC encoding and streaming transcription
     */
    async start(
      transcriptId: string,
      deviceId?: string,
      deviceName?: string,
      options: WebFlacRecordingOptions = {}
    ): Promise<void> {
      const currentState = get({ subscribe });
      if (currentState.state !== 'idle') {
        throw new Error(`Cannot start recording: current state is ${currentState.state}`);
      }

      // Clean up any existing recorder
      if (recorder) {
        recorder.dispose();
      }

      // Create new recorder with options
      recorder = new FlacStreamRecorder({
        deviceId,
        transcriptionEndpoint: options.transcriptionEndpoint || '/api/private/transcribe',
        chunkDurationMs: options.chunkDurationMs || 55000, // 55 seconds
        authToken: options.authToken,
        sessionId: transcriptId
      });

      // Set up event handlers
      recorder.setHandlers({
        onStateChange: (state) => {
          update((s) => ({ ...s, state }));
        },

        onDurationUpdate: (duration) => {
          update((s) => ({ ...s, duration }));
        },

        onChunkSent: (chunkId, _chunkDuration) => {
          pendingTranscriptions++;
          update((s) => ({
            ...s,
            lastChunkId: chunkId,
            isTranscribing: true
          }));
        },

        onTranscript: (chunk, fullTranscript) => {
          pendingTranscriptions = Math.max(0, pendingTranscriptions - 1);
          update((s) => ({
            ...s,
            fullTranscript,
            transcriptChunks: [...s.transcriptChunks, chunk],
            isTranscribing: pendingTranscriptions > 0
          }));
        },

        onProgress: (progress) => {
          // This is a new handler for incremental progress
          // We can store it or just let the subscribers know
          update((s) => ({
            ...s,
            fullTranscript: progress.output
          }));
        },

        onError: (error) => {
          pendingTranscriptions = Math.max(0, pendingTranscriptions - 1);
          update((s) => ({
            ...s,
            error: error.message,
            isTranscribing: pendingTranscriptions > 0
          }));
        }
      });

      // Update state before starting
      update((s) => ({
        ...s,
        transcriptId,
        deviceId: deviceId || null,
        deviceName: deviceName || null,
        startTime: Date.now(),
        error: null,
        audioBlob: null,
        fullTranscript: '',
        transcriptChunks: [],
        lastChunkId: null,
        isTranscribing: false
      }));

      // Start recording
      await recorder.start(deviceId);
    },

    /**
     * Stop recording and wait for all transcriptions to complete
     */
    async stop(): Promise<{
      transcript: string;
      chunks: TranscriptChunk[];
      audioBlob?: Blob;
    } | null> {
      if (!recorder) {
        console.warn('[WebFlacRecordingStore] No recorder to stop');
        return null;
      }

      const result = await recorder.stop();

      // Update final state
      if (result) {
        update((s) => ({
          ...s,
          fullTranscript: result.transcript,
          transcriptChunks: result.chunks,
          audioBlob: result.audioBlob || null,
          isTranscribing: false
        }));
      }

      return result;
    },

    /**
     * Pause recording
     */
    pause(): void {
      if (recorder) {
        recorder.pause();
      }
    },

    /**
     * Resume recording
     */
    resume(): void {
      if (recorder) {
        recorder.resume();
      }
    },

    /**
     * Abort recording without saving
     */
    abort(): void {
      if (recorder) {
        recorder.abort();
        recorder = null;
      }
      pendingTranscriptions = 0;
      set(initialSession);
    },

    /**
     * Reset the store to initial state
     */
    reset(): void {
      if (recorder) {
        recorder.dispose();
        recorder = null;
      }
      pendingTranscriptions = 0;
      set(initialSession);
    },

    /**
     * Get the current full transcript
     */
    getFullTranscript(): string {
      return get({ subscribe }).fullTranscript;
    },

    /**
     * Get all transcript chunks
     */
    getTranscriptChunks(): TranscriptChunk[] {
      return get({ subscribe }).transcriptChunks;
    },

    /**
     * Set the current state (e.g., 'summarizing')
     */
    setState(state: FlacStreamState): void {
      update((s) => ({ ...s, state }));
    },

    /**
     * Get the current recorder instance
     */
    getRecorder(): FlacStreamRecorder | null {
      return recorder;
    }
  };
};

// Export the singleton store
export const webFlacRecordingStore = createWebFlacRecordingStore();

// Derived stores for easy access to specific state
export const flacIsRecording = derived(webFlacRecordingStore, ($s) => $s.state === 'recording');
export const flacIsPaused = derived(webFlacRecordingStore, ($s) => $s.state === 'paused');
export const flacRecordingDuration = derived(webFlacRecordingStore, ($s) => $s.duration);
export const flacRecordingState = derived(webFlacRecordingStore, ($s) => $s.state);
export const flacFullTranscript = derived(webFlacRecordingStore, ($s) => $s.fullTranscript);
export const flacIsTranscribing = derived(webFlacRecordingStore, ($s) => $s.isTranscribing);
